// C:\rbpms\frontend\src\pages\leads\LeadsPage.tsx
import { useState, useEffect } from 'react';
import api from '../../utils/api';

// ── Types ────────────────────────────────────────────────────────────────────

interface Lead {
  lead_id: number; lead_code: string; full_name: string; mobile: string;
  alternate_mobile?: string; email?: string; source_channel: string;
  building_interest: string; plot_location?: string; plot_area_sqft?: number;
  budget_range?: string; enquiry_date: string; status: string; priority: string;
  assigned_to?: number; assigned_to_name?: string; next_followup_date?: string;
  lost_reason?: string; competitor_quoted?: string; competitor_rate?: number;
  converted_customer_id?: number; conversion_date?: string;
  notes?: string; created_at: string;
}

interface Customer {
  customer_id: number; customer_code: string; full_name: string; mobile: string;
  alternate_mobile?: string; whatsapp_number?: string; email?: string;
  pan_number?: string; aadhaar_number_enc?: string; gstin?: string; address_line1?: string; address_line2?: string;
  city?: string; state?: string; pincode?: string; source?: string;
  customer_type: string; is_active: boolean; lead_code?: string;
  building_interest?: string; budget_range?: string; project_count?: number; created_at: string;
}

interface Followup {
  followup_id: number; lead_id: number; followup_date: string; followup_type: string;
  summary: string; outcome: string; next_action?: string;
  next_followup_date?: string; done_by?: number; done_by_name?: string;
}

interface Stats {
  new_leads: string; contacted: string; site_visited: string; proposals: string;
  negotiation: string; converted: string; lost: string;
  followup_today: string; overdue_followups: string; total: string;
}

interface User { user_id: number; full_name: string; }

// ── Constants ────────────────────────────────────────────────────────────────

const STATUSES = ['NEW','CONTACTED','SITE_VISIT','ESTIMATE_SHARED','NEGOTIATION','CONVERTED','LOST','DORMANT'];
const PRIORITIES = ['LOW','MEDIUM','HIGH'];
const SOURCES = ['WALK_IN','REFERRAL','JUSTDIAL','FACEBOOK','GOOGLE','INSTAGRAM','WEBSITE','HOARDING','NEWSPAPER','OTHER'];
const BLDG_TYPES = ['IH','VI','SB','MB','HR','BP','TW'];
const BLDG_LABELS: Record<string, string> = {
  IH:'Independent House', VI:'Villa', SB:'Single Building',
  MB:'Multi-storey Block', HR:'High-rise', BP:'Builder Plot', TW:'Townhouse'
};
const FOLLOWUP_TYPES = ['CALL','VISIT','WHATSAPP','EMAIL','SITE_VISIT'];
const OUTCOMES = ['PENDING','INTERESTED','NOT_INTERESTED','CALLBACK','CONVERTED','NO_RESPONSE'];
const CUSTOMER_TYPES = ['INDIVIDUAL','COMPANY','NRI'];
const DOC_TYPES = ['AADHAAR','PAN','PASSPORT','SITE_PHOTO','PLAN','AGREEMENT','OTHER'];

const statusColor: Record<string, string> = {
  NEW:            'bg-blue-100 text-blue-700',
  CONTACTED:      'bg-yellow-100 text-yellow-700',
  SITE_VISIT:     'bg-purple-100 text-purple-700',
  ESTIMATE_SHARED:'bg-orange-100 text-orange-700',
  NEGOTIATION:    'bg-pink-100 text-pink-700',
  CONVERTED:      'bg-green-100 text-green-700',
  LOST:           'bg-red-100 text-red-700',
  DORMANT:        'bg-gray-100 text-gray-500',
};
const priorityColor: Record<string, string> = {
  LOW: 'bg-gray-100 text-gray-600',
  MEDIUM: 'bg-amber-100 text-amber-700',
  HIGH: 'bg-red-100 text-red-700',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const isOverdue = (d?: string) => d && new Date(d) < new Date() ? true : false;

// ── Modal Base ───────────────────────────────────────────────────────────────

const Modal = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    {children}
  </div>
);

const inp = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const sel = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";

// ── Add/Edit Lead Modal ──────────────────────────────────────────────────────

const LeadModal = ({ lead, users, onClose, onSave }: {
  lead?: Lead; users: User[]; onClose: () => void; onSave: () => void;
}) => {
  const [form, setForm] = useState({
    full_name: lead?.full_name || '', mobile: lead?.mobile || '',
    alternate_mobile: lead?.alternate_mobile || '', email: lead?.email || '',
    source_channel: lead?.source_channel || 'WALK_IN',
    building_interest: lead?.building_interest || 'IH',
    plot_location: lead?.plot_location || '', plot_area_sqft: lead?.plot_area_sqft || '',
    budget_range: lead?.budget_range || '', status: lead?.status || 'NEW',
    priority: lead?.priority || 'MEDIUM', assigned_to: lead?.assigned_to || '',
    next_followup_date: lead?.next_followup_date?.split('T')[0] || '',
    lost_reason: lead?.lost_reason || '', competitor_quoted: lead?.competitor_quoted || '',
    competitor_rate: lead?.competitor_rate || '', notes: lead?.notes || '',
    enquiry_date: lead?.enquiry_date?.split('T')[0] || new Date().toISOString().split('T')[0],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.full_name.trim()) return setError('Full name is required');
    if (!form.mobile.trim()) return setError('Mobile is required');
    setSaving(true); setError('');
    try {
      if (lead) {
        await api.put(`/leads/${lead.lead_id}`, form);
      } else {
        await api.post('/leads', form);
      }
      onSave();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Modal title={lead ? `Edit Lead — ${lead.lead_code}` : 'New Lead'} onClose={onClose}>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Full Name *">
          <input className={inp} value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Customer full name" />
        </Field>
        <Field label="Mobile *">
          <input className={inp} value={form.mobile} onChange={e => set('mobile', e.target.value)} placeholder="10-digit mobile" />
        </Field>
        <Field label="Alternate Mobile">
          <input className={inp} value={form.alternate_mobile} onChange={e => set('alternate_mobile', e.target.value)} />
        </Field>
        <Field label="Email">
          <input className={inp} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
        </Field>
        <Field label="Source Channel *">
          <select className={sel} value={form.source_channel} onChange={e => set('source_channel', e.target.value)}>
            {SOURCES.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
          </select>
        </Field>
        <Field label="Building Interest *">
          <select className={sel} value={form.building_interest} onChange={e => set('building_interest', e.target.value)}>
            {BLDG_TYPES.map(b => <option key={b} value={b}>{b} — {BLDG_LABELS[b]}</option>)}
          </select>
        </Field>
        <Field label="Plot Location">
          <input className={inp} value={form.plot_location} onChange={e => set('plot_location', e.target.value)} placeholder="Whitefield, Sarjapur..." />
        </Field>
        <Field label="Plot Area (sqft)">
          <input className={inp} type="number" value={form.plot_area_sqft} onChange={e => set('plot_area_sqft', e.target.value)} />
        </Field>
        <Field label="Budget Range">
          <input className={inp} value={form.budget_range} onChange={e => set('budget_range', e.target.value)} placeholder="e.g. 80L - 1Cr" />
        </Field>
        <Field label="Enquiry Date">
          <input className={inp} type="date" value={form.enquiry_date} onChange={e => set('enquiry_date', e.target.value)} />
        </Field>
        <Field label="Status">
          <select className={sel} value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Priority">
          <select className={sel} value={form.priority} onChange={e => set('priority', e.target.value)}>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Assigned To">
          <select className={sel} value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
            <option value="">— Unassigned —</option>
            {users.map(u => <option key={u.user_id} value={u.user_id}>{u.full_name}</option>)}
          </select>
        </Field>
        <Field label="Next Follow-up Date">
          <input className={inp} type="date" value={form.next_followup_date} onChange={e => set('next_followup_date', e.target.value)} />
        </Field>
        {form.status === 'LOST' && (
          <Field label="Lost Reason">
            <input className={inp} value={form.lost_reason} onChange={e => set('lost_reason', e.target.value)} />
          </Field>
        )}
        <Field label="Competitor Name">
          <input className={inp} value={form.competitor_quoted} onChange={e => set('competitor_quoted', e.target.value)} placeholder="Competitor name if any" />
        </Field>
        <Field label="Competitor Rate (₹/sqft)">
          <input className={inp} type="number" value={form.competitor_rate} onChange={e => set('competitor_rate', e.target.value)} />
        </Field>
        <div className="col-span-2">
          <Field label="Notes">
            <textarea className={inp} rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </Field>
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</p>}
      <div className="flex justify-end gap-3 mt-5">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
        <button onClick={handleSubmit} disabled={saving}
          className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving...' : (lead ? 'Update Lead' : 'Create Lead')}
        </button>
      </div>
    </Modal>
  );
};

// ── Convert Lead Modal ───────────────────────────────────────────────────────

const ConvertModal = ({ lead, onClose, onSave }: { lead: Lead; onClose: () => void; onSave: () => void }) => {
  const [form, setForm] = useState({
    customer_type: 'INDIVIDUAL', whatsapp_number: lead.mobile,
    address_line1: '', city: 'Bengaluru', state: 'Karnataka', pincode: '',
    pan_number: '', aadhaar_number_enc: '', gstin: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleConvert = async () => {
    setSaving(true); setError('');
    try {
      await api.post(`/leads/${lead.lead_id}/convert`, form);
      onSave();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Conversion failed');
    } finally { setSaving(false); }
  };

  return (
    <Modal title={`Convert Lead → Customer: ${lead.full_name}`} onClose={onClose}>
      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
        Lead <strong>{lead.lead_code}</strong> will be marked as CONVERTED and a Customer record will be created.
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Customer Type">
          <select className={sel} value={form.customer_type} onChange={e => set('customer_type', e.target.value)}>
            {CUSTOMER_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="WhatsApp Number">
          <input className={inp} value={form.whatsapp_number} onChange={e => set('whatsapp_number', e.target.value)} />
        </Field>
        <Field label="PAN Number">
          <input className={inp} value={form.pan_number} onChange={e => set('pan_number', e.target.value)} placeholder="ABCDE1234F" />
        </Field>
        <Field label="Aadhaar (last 4 digits)">
          <input className={inp} value={form.aadhaar_number_enc} onChange={e => set('aadhaar_number_enc', e.target.value)} />
        </Field>
        <Field label="GSTIN (if company)">
          <input className={inp} value={form.gstin} onChange={e => set('gstin', e.target.value)} />
        </Field>
        <Field label="Pincode">
          <input className={inp} value={form.pincode} onChange={e => set('pincode', e.target.value)} />
        </Field>
        <div className="col-span-2">
          <Field label="Address Line 1">
            <input className={inp} value={form.address_line1} onChange={e => set('address_line1', e.target.value)} />
          </Field>
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</p>}
      <div className="flex justify-end gap-3 mt-5">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
        <button onClick={handleConvert} disabled={saving}
          className="px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
          {saving ? 'Converting...' : '✓ Convert to Customer'}
        </button>
      </div>
    </Modal>
  );
};

// ── Followup Modal ───────────────────────────────────────────────────────────

const FollowupModal = ({ lead, onClose, onSave }: { lead: Lead; onClose: () => void; onSave: () => void }) => {
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [form, setForm] = useState({
    followup_type: 'CALL', summary: '', outcome: 'PENDING',
    next_action: '', next_followup_date: '',
    followup_date: new Date().toISOString().slice(0, 16),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get(`/leads/${lead.lead_id}/followups`).then(r => setFollowups(r.data.data));
  }, [lead.lead_id]);

  const handleSave = async () => {
    if (!form.summary.trim()) return setError('Summary is required');
    setSaving(true); setError('');
    try {
      await api.post(`/leads/${lead.lead_id}/followups`, form);
      onSave();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Modal title={`Follow-ups — ${lead.lead_code} · ${lead.full_name}`} onClose={onClose}>
      {/* History */}
      {followups.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-gray-600 mb-2">History ({followups.length})</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {followups.map(f => (
              <div key={f.followup_id} className="border border-gray-100 rounded-lg p-3 bg-gray-50 text-sm">
                <div className="flex justify-between items-start">
                  <span className="font-medium text-gray-700">{f.followup_type} — {fmt(f.followup_date)}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    f.outcome === 'INTERESTED' ? 'bg-green-100 text-green-700' :
                    f.outcome === 'NOT_INTERESTED' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{f.outcome}</span>
                </div>
                <p className="text-gray-600 mt-1">{f.summary}</p>
                {f.next_action && <p className="text-blue-600 mt-1 text-xs">→ {f.next_action}</p>}
                {f.done_by_name && <p className="text-gray-400 text-xs mt-1">By: {f.done_by_name}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* New Followup */}
      <h3 className="text-sm font-semibold text-gray-600 mb-3">Add Follow-up</h3>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Type *">
          <select className={sel} value={form.followup_type} onChange={e => set('followup_type', e.target.value)}>
            {FOLLOWUP_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Date & Time">
          <input className={inp} type="datetime-local" value={form.followup_date} onChange={e => set('followup_date', e.target.value)} />
        </Field>
        <Field label="Outcome *">
          <select className={sel} value={form.outcome} onChange={e => set('outcome', e.target.value)}>
            {OUTCOMES.map(o => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Next Follow-up Date">
          <input className={inp} type="date" value={form.next_followup_date} onChange={e => set('next_followup_date', e.target.value)} />
        </Field>
        <div className="col-span-2">
          <Field label="Summary *">
            <textarea className={inp} rows={2} value={form.summary} onChange={e => set('summary', e.target.value)} placeholder="What was discussed..." />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Next Action">
            <input className={inp} value={form.next_action} onChange={e => set('next_action', e.target.value)} placeholder="What to do next..." />
          </Field>
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</p>}
      <div className="flex justify-end gap-3 mt-5">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
        <button onClick={handleSave} disabled={saving}
          className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Add Follow-up'}
        </button>
      </div>
    </Modal>
  );
};

// ── Customer Modal ───────────────────────────────────────────────────────────

const CustomerModal = ({ customer, onClose, onSave }: {
  customer?: Customer; onClose: () => void; onSave: () => void;
}) => {
  const [form, setForm] = useState({
    full_name: customer?.full_name || '', mobile: customer?.mobile || '',
    alternate_mobile: customer?.alternate_mobile || '',
    whatsapp_number: customer?.whatsapp_number || '',
    email: customer?.email || '', pan_number: customer?.pan_number || '',
    aadhaar_number_enc: customer?.aadhaar_number_enc || '',
    gstin: customer?.gstin || '', address_line1: customer?.address_line1 || '',
    address_line2: customer?.address_line2 || '',
    city: customer?.city || 'Bengaluru', state: customer?.state || 'Karnataka',
    pincode: customer?.pincode || '', customer_type: customer?.customer_type || 'INDIVIDUAL',
    is_active: customer?.is_active !== undefined ? customer.is_active : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.full_name.trim()) return setError('Full name is required');
    if (!form.mobile.trim()) return setError('Mobile is required');
    setSaving(true); setError('');
    try {
      if (customer) {
        await api.put(`/customers/${customer.customer_id}`, form);
      } else {
        await api.post('/customers', form);
      }
      onSave();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Modal title={customer ? `Edit Customer — ${customer.customer_code}` : 'New Customer'} onClose={onClose}>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Full Name *">
          <input className={inp} value={form.full_name} onChange={e => set('full_name', e.target.value)} />
        </Field>
        <Field label="Customer Type">
          <select className={sel} value={form.customer_type} onChange={e => set('customer_type', e.target.value)}>
            {CUSTOMER_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Mobile *">
          <input className={inp} value={form.mobile} onChange={e => set('mobile', e.target.value)} />
        </Field>
        <Field label="WhatsApp Number">
          <input className={inp} value={form.whatsapp_number} onChange={e => set('whatsapp_number', e.target.value)} />
        </Field>
        <Field label="Alternate Mobile">
          <input className={inp} value={form.alternate_mobile} onChange={e => set('alternate_mobile', e.target.value)} />
        </Field>
        <Field label="Email">
          <input className={inp} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
        </Field>
        <Field label="PAN Number">
          <input className={inp} value={form.pan_number} onChange={e => set('pan_number', e.target.value)} placeholder="ABCDE1234F" />
        </Field>
        <Field label="Aadhaar (last 4)">
          <input className={inp} value={form.aadhaar_number_enc} onChange={e => set('aadhaar_number_enc', e.target.value)} />
        </Field>
        <Field label="GSTIN">
          <input className={inp} value={form.gstin} onChange={e => set('gstin', e.target.value)} />
        </Field>
        <Field label="Pincode">
          <input className={inp} value={form.pincode} onChange={e => set('pincode', e.target.value)} />
        </Field>
        <div className="col-span-2">
          <Field label="Address Line 1">
            <input className={inp} value={form.address_line1} onChange={e => set('address_line1', e.target.value)} />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Address Line 2">
            <input className={inp} value={form.address_line2} onChange={e => set('address_line2', e.target.value)} />
          </Field>
        </div>
        <Field label="City">
          <input className={inp} value={form.city} onChange={e => set('city', e.target.value)} />
        </Field>
        <Field label="State">
          <input className={inp} value={form.state} onChange={e => set('state', e.target.value)} />
        </Field>
        {customer && (
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="is_active" checked={form.is_active}
              onChange={e => set('is_active', e.target.checked)} className="rounded" />
            <label htmlFor="is_active" className="text-sm text-gray-700">Active Customer</label>
          </div>
        )}
      </div>
      {error && <p className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</p>}
      <div className="flex justify-end gap-3 mt-5">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
        <button onClick={handleSubmit} disabled={saving}
          className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving...' : (customer ? 'Update' : 'Create Customer')}
        </button>
      </div>
    </Modal>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────────

export default function LeadsPage() {
  const [tab, setTab] = useState<'leads' | 'customers' | 'today'>('leads');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [todayLeads, setTodayLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [custSearch, setCustSearch] = useState('');

  // Modals
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [editLead, setEditLead] = useState<Lead | undefined>();
  const [convertLead, setConvertLead] = useState<Lead | undefined>();
  const [followupLead, setFollowupLead] = useState<Lead | undefined>();
  const [showCustModal, setShowCustModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | undefined>();

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (search) params.append('search', search);
      const r = await api.get(`/leads?${params}`);
      setLeads(r.data.data);
    } finally { setLoading(false); }
  };

  const loadCustomers = async () => {
    const params = new URLSearchParams();
    if (custSearch) params.append('search', custSearch);
    const r = await api.get(`/customers?${params}`);
    setCustomers(r.data.data);
  };

  const loadStats = async () => {
    const r = await api.get('/leads/stats');
    setStats(r.data.data);
  };

  const loadTodayLeads = async () => {
    const r = await api.get('/leads?status=NEW&priority=HIGH');
    const all = await api.get('/leads');
    const today = all.data.data.filter((l: Lead) =>
      l.next_followup_date?.split('T')[0] === new Date().toISOString().split('T')[0]
    );
    setTodayLeads(today);
  };

  const loadUsers = async () => {
    const r = await api.get('/users');
    setUsers(r.data.data || []);
  };

  useEffect(() => {
    loadUsers();
    loadStats();
    loadLeads();
    loadCustomers();
    loadTodayLeads();
  }, []);

  useEffect(() => { loadLeads(); }, [statusFilter, priorityFilter]);
  useEffect(() => { loadCustomers(); }, [custSearch]);

  const handleSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') loadLeads();
  };

  const onLeadSaved = () => {
    setShowLeadModal(false); setEditLead(undefined);
    loadLeads(); loadStats();
  };

  const onFollowupSaved = () => {
    setFollowupLead(undefined); loadLeads(); loadStats();
  };

  const onConvertSaved = () => {
    setConvertLead(undefined); loadLeads(); loadCustomers(); loadStats();
  };

  const onCustSaved = () => {
    setShowCustModal(false); setEditCustomer(undefined); loadCustomers();
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lead & Customer Master</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage sales pipeline and customer records</p>
        </div>
        <button
          onClick={() => tab === 'leads' ? setShowLeadModal(true) : setShowCustModal(true)}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 flex items-center gap-2">
          + {tab === 'customers' ? 'New Customer' : 'New Lead'}
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total Leads', val: stats.total, color: 'bg-gray-700' },
            { label: 'New', val: stats.new_leads, color: 'bg-blue-600' },
            { label: 'Converted', val: stats.converted, color: 'bg-green-600' },
            { label: 'Follow-up Today', val: stats.followup_today, color: 'bg-amber-500' },
            { label: 'Overdue', val: stats.overdue_followups, color: 'bg-red-600' },
          ].map(s => (
            <div key={s.label} className={`${s.color} text-white rounded-xl p-4`}>
              <div className="text-2xl font-bold">{s.val}</div>
              <div className="text-xs mt-1 opacity-80">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-white border border-gray-200 rounded-xl p-1 w-fit">
        {[
          { key: 'leads', label: `Leads (${leads.length})` },
          { key: 'customers', label: `Customers (${customers.length})` },
          { key: 'today', label: `Follow-up Today (${todayLeads.length})` },
        ].map(t => (
          <button key={t.key}
            onClick={() => setTab(t.key as any)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              tab === t.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Leads Tab ── */}
      {tab === 'leads' && (
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Filters */}
          <div className="flex gap-3 p-4 border-b border-gray-100 flex-wrap">
            <input className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-56"
              placeholder="Search name / mobile / code..." value={search}
              onChange={e => setSearch(e.target.value)} onKeyDown={handleSearch} />
            <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
              value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
              value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
              <option value="">All Priorities</option>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
            <button onClick={loadLeads}
              className="px-3 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">🔍 Search</button>
          </div>
          {/* Table */}
          {loading ? (
            <div className="p-12 text-center text-gray-400">Loading leads...</div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              No leads found. Click <strong>+ New Lead</strong> to add your first lead.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left">Lead</th>
                    <th className="px-4 py-3 text-left">Contact</th>
                    <th className="px-4 py-3 text-left">Interest</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Priority</th>
                    <th className="px-4 py-3 text-left">Assigned</th>
                    <th className="px-4 py-3 text-left">Next F/U</th>
                    <th className="px-4 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {leads.map(l => (
                    <tr key={l.lead_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{l.full_name}</div>
                        <div className="text-xs text-gray-400">{l.lead_code} · {fmt(l.enquiry_date)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700">{l.mobile}</div>
                        <div className="text-xs text-gray-400">{l.source_channel?.replace('_',' ')}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-700">{BLDG_LABELS[l.building_interest] || l.building_interest}</div>
                        {l.budget_range && <div className="text-xs text-gray-400">{l.budget_range}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor[l.status]}`}>
                          {l.status?.replace(/_/g,' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${priorityColor[l.priority]}`}>
                          {l.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{l.assigned_to_name || '—'}</td>
                      <td className="px-4 py-3">
                        {l.next_followup_date ? (
                          <span className={`text-xs ${isOverdue(l.next_followup_date) && !['CONVERTED','LOST'].includes(l.status) ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                            {fmt(l.next_followup_date)}
                            {isOverdue(l.next_followup_date) && !['CONVERTED','LOST'].includes(l.status) && ' ⚠'}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => { setEditLead(l); setShowLeadModal(true); }}
                            className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100">Edit</button>
                          <button onClick={() => setFollowupLead(l)}
                            className="px-2 py-1 text-xs bg-purple-50 text-purple-600 rounded hover:bg-purple-100">F/U</button>
                          {!['CONVERTED','LOST'].includes(l.status) && (
                            <button onClick={() => setConvertLead(l)}
                              className="px-2 py-1 text-xs bg-green-50 text-green-600 rounded hover:bg-green-100">→ Cust</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Customers Tab ── */}
      {tab === 'customers' && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex gap-3 p-4 border-b border-gray-100">
            <input className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-56"
              placeholder="Search name / mobile / code..."
              value={custSearch} onChange={e => setCustSearch(e.target.value)} />
          </div>
          {customers.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              No customers yet. Convert a lead or click <strong>+ New Customer</strong>.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-left">Contact</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Location</th>
                    <th className="px-4 py-3 text-left">Projects</th>
                    <th className="px-4 py-3 text-left">Lead Ref</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {customers.map(c => (
                    <tr key={c.customer_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{c.full_name}</div>
                        <div className="text-xs text-gray-400">{c.customer_code}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700">{c.mobile}</div>
                        {c.whatsapp_number && <div className="text-xs text-green-600">WA: {c.whatsapp_number}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">{c.customer_type}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{c.city || '—'}{c.state ? `, ${c.state}` : ''}</td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-gray-700">{c.project_count || 0}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">{c.lead_code || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => { setEditCustomer(c); setShowCustModal(true); }}
                          className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100">Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Follow-up Today Tab ── */}
      {tab === 'today' && (
        <div className="bg-white rounded-xl border border-gray-200">
          {todayLeads.length === 0 ? (
            <div className="p-12 text-center text-gray-400">No follow-ups scheduled for today 🎉</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left">Lead</th>
                    <th className="px-4 py-3 text-left">Mobile</th>
                    <th className="px-4 py-3 text-left">Interest</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Assigned To</th>
                    <th className="px-4 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {todayLeads.map(l => (
                    <tr key={l.lead_id} className="hover:bg-amber-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{l.full_name}</div>
                        <div className="text-xs text-gray-400">{l.lead_code}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{l.mobile}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{BLDG_LABELS[l.building_interest]}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor[l.status]}`}>
                          {l.status?.replace(/_/g,' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{l.assigned_to_name || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => setFollowupLead(l)}
                            className="px-3 py-1 text-xs bg-amber-500 text-white rounded hover:bg-amber-600">
                            Record F/U
                          </button>
                          <button onClick={() => { setEditLead(l); setShowLeadModal(true); }}
                            className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100">Edit</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ── */}
      {showLeadModal && (
        <LeadModal lead={editLead} users={users} onClose={() => { setShowLeadModal(false); setEditLead(undefined); }} onSave={onLeadSaved} />
      )}
      {convertLead && (
        <ConvertModal lead={convertLead} onClose={() => setConvertLead(undefined)} onSave={onConvertSaved} />
      )}
      {followupLead && (
        <FollowupModal lead={followupLead} onClose={() => setFollowupLead(undefined)} onSave={onFollowupSaved} />
      )}
      {showCustModal && (
        <CustomerModal customer={editCustomer} onClose={() => { setShowCustModal(false); setEditCustomer(undefined); }} onSave={onCustSaved} />
      )}
    </div>
  );
}

